// Adaptador ÚNICO del molde de incidencia del backend.
//
// El backend (endpoints REST) devuelve SIEMPRE la forma anidada, envoltorio { data }:
//   { id, title, description, type:{id,name}, status:{id,slug,name,color,is_closed},
//     priority:{id,slug,name,color}, site:{id,name,lat,lng,address,zone:{id,name}},
//     asset:{id,name,station_code}, assigned:{to,name,date,accepted_date},
//     created:{at,by:{id,name}}, closed:{at,by:{id,name},comment},
//     counts:{comments,attachments}, technicians:[...], photos:[...], comments:[...] }
//
// Los eventos WebSocket (logistics.incident.*) emiten el MODELO crudo (con
// accesores: status_slug, priority_level, assigned_to_name, accepted_date,
// final_comment, photos, y relaciones status/priority/site planas). Por eso
// esta función tolera AMBAS formas y produce siempre el mismo objeto estable.
// Es el único punto que conoce las formas; los componentes leen de aquí.

const PRIORITY_LEVEL_BY_SLUG = { alta: 1, media: 2, baja: 3 }

const pick = (...vals) => {
  for (const v of vals) if (v !== undefined && v !== null) return v
  return null
}

export function normalizeIncident(raw) {
  if (!raw || typeof raw !== 'object') return raw

  const status = raw.status || {}
  const priority = raw.priority || {}
  const site = raw.site || {}
  const zone = site.zone || raw.zone || null
  const asset = raw.asset || null
  const type = raw.type || null
  const assigned = raw.assigned || {}
  const created = raw.created || {}
  const closed = raw.closed || {}
  const counts = raw.counts || {}
  const supervision = raw.supervision || {}
  const clientsWaiting = raw.clients_waiting_flag || {}

  const statusSlug = pick(status.slug, raw.status_slug)
  const statusName = pick(status.name, raw.status_name)
  const statusColor = pick(status.color, raw.status_color)
  const statusClosed = !!pick(status.is_closed, raw.is_closed, raw.status_is_closed, false)

  const prioritySlug = pick(priority.slug, raw.priority_slug, raw.priority_level)
  const priorityName = pick(priority.name, raw.priority_name)
  // level numérico para ordenar (priority.id, o derivado del slug)
  const priorityLevel = pick(
    priority.id,
    typeof priority.level === 'number' ? priority.level : null,
    raw.priority_id,
    PRIORITY_LEVEL_BY_SLUG[prioritySlug],
    99
  )

  const lat = pick(site.lat, site.latitude, raw.lat, raw.latitude)
  const lng = pick(site.lng, site.longitude, raw.lng, raw.longitude)
  const siteId = pick(site.id, raw.site_id)
  const siteName = pick(site.name, raw.site_name)
  const zoneId = pick(zone?.id, raw.zone_id, site.zone_id)
  const zoneName = pick(zone?.name, raw.zone_name)

  const technicians = Array.isArray(raw.technicians)
    ? raw.technicians
    : (Array.isArray(raw.assigned_technicians) ? raw.assigned_technicians : [])

  return {
    id: raw.id,
    title: raw.title ?? null,
    description: raw.description ?? null,

    // --- relaciones anidadas (forma canónica) ---
    status: {
      id: pick(status.id, raw.status_id),
      slug: statusSlug,
      name: statusName,
      color: statusColor,
      is_closed: statusClosed,
    },
    priority: {
      id: pick(priority.id, raw.priority_id),
      slug: prioritySlug,
      name: priorityName,
      color: pick(priority.color, raw.priority_color),
      level: priorityLevel,
    },
    site: {
      id: siteId,
      name: siteName,
      lat,
      lng,
      latitude: lat,
      longitude: lng,
      address: pick(site.address, raw.address),
      zone: (zoneId || zoneName) ? { id: zoneId, name: zoneName } : null,
      zone_id: zoneId,
    },
    type: type
      ? {
          id: type.id ?? null,
          name: type.name ?? null,
          icon: type.icon ?? null,
          color: type.color ?? null,
        }
      : null,
    asset: asset
      ? {
          id: asset.id ?? null,
          name: asset.name ?? null,
          station_code: asset.station_code ?? null,
          model: asset.model ?? null,
          image_filename: asset.image_filename ?? null,
        }
      : null,

    // --- alias planos (compatibilidad con componentes existentes) ---
    status_id: pick(status.id, raw.status_id),
    status_slug: statusSlug,
    status_name: statusName,
    status_color: statusColor,
    is_closed: statusClosed,

    priority_id: pick(priority.id, raw.priority_id),
    priority_slug: prioritySlug,
    priority_name: priorityName,
    priority_level: prioritySlug,

    site_id: siteId,
    site_name: siteName,
    lat,
    lng,
    latitude: lat,
    longitude: lng,
    zone_id: zoneId,
    zone_name: zoneName,

    type_id: pick(type?.id, raw.type_id),
    type_name: pick(type?.name, raw.type_name, typeof raw.type === 'string' ? raw.type : null),
    asset_id: pick(asset?.id, raw.asset_id),
    asset_name: pick(asset?.name, raw.asset_name),

    assigned_to: pick(assigned.to, raw.assigned_to),
    assigned_to_name: pick(assigned.name, raw.assigned_to_name),
    assigned_date: pick(assigned.date, raw.assigned_date),
    accepted_date: pick(assigned.accepted_date, raw.accepted_date),

    created_at: pick(created.at, raw.created_at),
    created_by: pick(created.by?.id, raw.created_by),
    created_by_name: pick(created.by?.name, raw.created_by_name),

    // Última actividad (creación o último comentario, lo que sea más reciente).
    // El backend la calcula en los listados; si no llega, cae a created_at.
    last_activity_at: pick(raw.last_activity_at, created.at, raw.created_at),

    closed_at: pick(closed.at, raw.closed_at),
    closed_by_id: pick(closed.by?.id, raw.closed_by),
    closed_by_name: pick(closed.by?.name, raw.closed_by_name),
    closed_comment: pick(closed.comment, raw.final_comment, raw.closed_comment),

    time_pending: pick(raw.time_pending),

    requires_supervision: !!pick(raw.requires_supervision, false),
    supervision: {
      // none | pending | reviewed
      state: pick(supervision.state, raw.requires_supervision ? 'pending' : 'none'),
      note: pick(supervision.note, raw.supervision_note, null),
      requested_at: pick(supervision.requested?.at, raw.supervision_requested_at, null),
      requested_by_id: pick(supervision.requested?.by?.id, raw.supervision_requested_by, null),
      requested_by_name: pick(supervision.requested?.by?.name, null),
      reviewed_at: pick(supervision.reviewed?.at, raw.supervision_reviewed_at, null),
      reviewed_by_id: pick(supervision.reviewed?.by?.id, raw.supervision_reviewed_by, null),
      reviewed_by_name: pick(supervision.reviewed?.by?.name, null),
      resolution: pick(supervision.reviewed?.resolution, raw.supervision_resolution, null),
    },

    clients_waiting: !!pick(raw.clients_waiting, false),
    clients_waiting_flag: {
      // none | pending | reviewed
      state: pick(clientsWaiting.state, raw.clients_waiting ? 'pending' : 'none'),
      note: pick(clientsWaiting.note, raw.clients_waiting_note, null),
      requested_at: pick(clientsWaiting.requested?.at, raw.clients_waiting_requested_at, null),
      requested_by_id: pick(clientsWaiting.requested?.by?.id, raw.clients_waiting_requested_by, null),
      requested_by_name: pick(clientsWaiting.requested?.by?.name, null),
      reviewed_at: pick(clientsWaiting.reviewed?.at, raw.clients_waiting_reviewed_at, null),
      reviewed_by_id: pick(clientsWaiting.reviewed?.by?.id, raw.clients_waiting_reviewed_by, null),
      reviewed_by_name: pick(clientsWaiting.reviewed?.by?.name, null),
      resolution: pick(clientsWaiting.reviewed?.resolution, raw.clients_waiting_resolution, null),
    },

    technicians,
    assigned_technicians: technicians,

    // Técnicos que han tenido que ver con la avería ALGUNA VEZ: los asignados
    // ahora + los que la rechazaron, la traspasaron o a los que se la quitaron
    // (backend: incident_technician_events). Lo usa el filtro por técnico del
    // tablero. Si el endpoint no los manda, valen los de ahora.
    involved_technician_ids: Array.isArray(raw.involved_technician_ids)
      ? raw.involved_technician_ids
      : technicians.map((t) => t.id).filter((id) => id != null),
    technician_history: Array.isArray(raw.technician_history) ? raw.technician_history : [],

    photos: Array.isArray(raw.photos) ? raw.photos : [],
    comments: Array.isArray(raw.comments) ? raw.comments : [],
    counts: {
      comments: pick(counts.comments, raw.comments_count, 0),
      attachments: pick(counts.attachments, raw.attachments_count, 0),
    },
  }
}

export function normalizeIncidentList(arr) {
  return Array.isArray(arr) ? arr.map(normalizeIncident) : []
}

// ¿Tiene "clientes esperando" PENDIENTE (activo y sin revisar)? Estas suben
// siempre a lo más alto de las listas. Si ya está revisado/atendido, no fuerza
// posición (se ordena por actividad como el resto).
export function incidentWaitingPending(i) {
  if (!i) return false
  const st = i.clients_waiting_flag?.state
  if (st) return st === 'pending'
  return !!i.clients_waiting
}

// ¿Tiene "requiere supervisión" PENDIENTE (activa y sin revisar)? Va justo
// debajo de "clientes esperando" en el orden de las listas.
export function incidentSupervisionPending(i) {
  if (!i) return false
  const st = i.supervision?.state
  if (st) return st === 'pending'
  return !!i.requires_supervision
}

// Una cerrada con supervisión pendiente sigue en el tablero (Requiere atención)
// hasta que un responsable la revise; solo entonces pasa al historial.
export function incidentInClosedHistory(i) {
  if (!i) return false
  const closed =
    typeof i.status === 'object' ? !!i.status?.is_closed : !!(i.is_closed ?? false)
  return closed && !incidentSupervisionPending(i)
}

// Timestamp (ms) de la última actividad de la incidencia: lo más reciente entre
// su creación y su último comentario. Un comentario nuevo "pisa" la fecha de
// creación y la sube en las listas ordenadas por fecha.
export function incidentLastActivityTs(i) {
  const v = i?.last_activity_at || i?.created_at
  const t = v ? new Date(v).getTime() : 0
  return Number.isFinite(t) ? t : 0
}

// Comparador ÚNICO de las listas de incidencias:
//   1) clientes esperando (pendiente) arriba del todo
//   2) requiere supervisión (pendiente)
//   3) lo más reciente por actividad (un comentario nuevo pisa la creación)
// Sin prioridad: manda la fecha/actividad.
export function compareIncidentsByActivity(a, b) {
  const wa = incidentWaitingPending(a)
  const wb = incidentWaitingPending(b)
  if (wa !== wb) return wa ? -1 : 1
  const sa = incidentSupervisionPending(a)
  const sb = incidentSupervisionPending(b)
  if (sa !== sb) return sa ? -1 : 1
  return incidentLastActivityTs(b) - incidentLastActivityTs(a)
}

// Etapa del ciclo de vida de una incidencia, derivada de su estado y técnicos.
// Fuente ÚNICA de verdad para columnas del tablero y el stepper del modal.
//   pending  → abierta, sin técnico asignado
//   assigned → abierta, con técnico(s) pero ninguno la ha aceptado
//   accepted → abierta y aceptada (en curso)
//   resolved → cerrada
export function incidentLifecycleBucket(i) {
  if (!i) return 'pending'
  const closed =
    typeof i.status === 'object' ? !!i.status?.is_closed : !!(i.is_closed ?? false)
  if (closed) return 'resolved'
  const techs = i.technicians || i.assigned_technicians || []
  const hasTech = !!i.assigned_to || techs.length > 0
  if (!hasTech) return 'pending'
  const accepted = !!i.accepted_date || techs.some((t) => t.pivot?.accepted_at)
  return accepted ? 'accepted' : 'assigned'
}

// Pasos del ciclo de vida en orden, para el stepper del detalle.
export const LIFECYCLE_STEPS = [
  { key: 'pending', label: 'Creada' },
  { key: 'assigned', label: 'Asignada' },
  { key: 'accepted', label: 'En curso' },
  { key: 'resolved', label: 'Resuelta' },
]

// Color + etiqueta por etapa del ciclo de vida. Fuente ÚNICA de verdad para el
// fondo de las cards y el badge de estado del detalle.
//   Sin asignar → rojo · Asignada → amarillo · Aceptada → azul · Resuelta → verde
export const LIFECYCLE_STYLES = {
  pending: { key: 'pending', label: 'Sin asignar', color: '#ef4444' },
  assigned: { key: 'assigned', label: 'Asignada', color: '#eab308' },
  accepted: { key: 'accepted', label: 'Aceptada', color: '#3b82f6' },
  resolved: { key: 'resolved', label: 'Resuelta', color: '#22c55e' },
}

// Estilo visual (color + etiqueta) del estado de asignación de una incidencia.
export function incidentLifecycleStyle(i) {
  return LIFECYCLE_STYLES[incidentLifecycleBucket(i)] || LIFECYCLE_STYLES.pending
}

// ── Implicación pasada de un técnico ─────────────────────────────────────────
// Filtrar por técnico no mira sólo a quién lleva la avería ahora: también cuenta
// lo que pasó por sus manos (la rechazó, la traspasó o se la quitaron). El
// backend manda ese rastro en `technician_history` / `involved_technician_ids`
// (tabla incident_technician_events).

export const INCIDENT_EXIT_EVENTS = ['rejected', 'removed', 'recurred', 'transferred_out']

/**
 * De los técnicos indicados, los que tuvieron que ver con la avería pero YA NO
 * la llevan. Devuelve [{ id, name, event, at, note, times }] para poder
 * explicarlo en la tarjeta.
 */
export function pastInvolvement(incident, ids = [], people = []) {
  const history = incident?.technician_history || []
  if (history.length === 0 || ids.length === 0) return []

  const current = new Set(
    (incident.technicians || incident.assigned_technicians || []).map((t) => t.id)
  )
  if (incident.assigned?.to) current.add(incident.assigned.to)
  if (incident.assigned_to) current.add(incident.assigned_to)

  return ids.flatMap((id) => {
    if (current.has(id)) return []
    const mine = history.filter((e) => e.user_id === id)
    if (mine.length === 0) return []
    // El último evento de salida es el que mejor describe la relación.
    const exit = [...mine].reverse().find((e) => INCIDENT_EXIT_EVENTS.includes(e.event))
    return [{
      id,
      name: people.find((p) => p.id === id)?.name || null,
      event: exit?.event || null,
      at: exit?.at || null,
      note: exit?.note || null,
      times: exit ? mine.filter((e) => e.event === exit.event).length : 0,
    }]
  })
}

/**
 * Marca las incidencias que entran en un listado SÓLO por historia, para que la
 * tarjeta pueda decir el porqué (si no, parecería que el filtro trae averías
 * de otro técnico).
 */
export function markPastInvolvement(list, ids = [], people = []) {
  if (ids.length === 0) return list
  return list.map((i) => {
    const past = pastInvolvement(i, ids, people)
    return past.length > 0 ? { ...i, past_involvement: past } : i
  })
}

/* ------------------------------------------------------------------ *
 * Cierre de incidencias: el motivo es OBLIGATORIO
 * ------------------------------------------------------------------ */

// Una incidencia cerrada sin explicación no se puede auditar después: ni el
// salón ni el siguiente técnico saben qué se hizo. El servidor valida lo mismo
// (min:5) en /incidents/{id}/resolve y en /incidents/{id}/close; esto es para
// avisar antes y no dejar que el usuario llegue hasta el 422.
export const CLOSE_REASON_MIN = 5

export const isCloseReasonValid = (text) =>
  (text || '').trim().length >= CLOSE_REASON_MIN

// Qué falta para poder cerrar (null = está listo).
export function closeReasonHint(text) {
  const n = (text || '').trim().length
  if (n === 0) return 'Obligatorio: explica qué se ha hecho para cerrarla.'
  if (n < CLOSE_REASON_MIN)
    return `Un poco más de detalle (mínimo ${CLOSE_REASON_MIN} caracteres).`
  return null
}
